// src/pages/AnalyticsPage.jsx
import { useState, useEffect ,Suspense , lazy } from 'react';
import { 
  Grid,
  Paper,
  Typography,
  IconButton,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Box
} from '@mui/material';
import { 
  LineChart,
  BarChart,
  PieChart,
  ScatterChart
} from '@mui/x-charts';
import { 
  DateRange,
  FilterList,
  Timeline,
  Map,
  Warning,
  Download
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { useTheme } from '@mui/material/styles';


// Dynamic import for map to prevent SSR issues
const ReactLeafletMap = lazy(() => import('../components/common/MapComponent'));

const AnalyticsPage = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('week');
  const [damageType, setDamageType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);

  // Mock data - replace with API calls
  const mockData = {
    trends: {
      labels: Array.from({ length: 7 }, (_, i) => format(subDays(new Date(), 6 - i), 'dd MMM')),
      highSeverity: [2, 3, 1, 4, 2, 5, 3],
      mediumSeverity: [5, 4, 6, 3, 7, 4, 6],
      lowSeverity: [8, 7, 5, 9, 6, 7, 8]
    },
    distribution: [
      { id: 0, value: 35, label: 'Cracks' },
      { id: 1, value: 25, label: 'Settlement' },
      { id: 2, value: 40, label: 'Other' }
    ],
    responseTimes: {
      average: 48,
      best: 12,
      worst: 96
    },
    geoData: [
      { lat: 51.505, lng: -0.09, severity: 'high' },
      { lat: 51.51, lng: -0.1, severity: 'medium' }
    ]
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setTimeout(() => {
        setAnalyticsData(mockData);
        setLoading(false);
      }, 1000);
    };
    fetchData();
  }, [timeRange, damageType]);

  const handleExport = (format) => {
    console.log(`Exporting data as ${format}`);
    // Implement export functionality
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading analytics data...
        </Typography>
      </Paper>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }}>
      {/* Header */}
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Damage Analytics
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                label="Time Range"
              >
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="year">Last Year</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Damage Type</InputLabel>
              <Select
                value={damageType}
                onChange={(e) => setDamageType(e.target.value)}
                label="Damage Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="crack">Cracks</MenuItem>
                <MenuItem value="settlement">Settlement</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={() => handleExport('pdf')}>
              <Download />
            </IconButton>
          </Stack>
        </Stack>
      </Grid>

      {/* Key Metrics */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Average Response Time
          </Typography>
          <Typography variant="h2" color="primary">
            {analyticsData.responseTimes.average}h
          </Typography>
          <Stack direction="row" justifyContent="space-around" sx={{ mt: 2 }}>
            <div>
              <Typography variant="caption">Best</Typography>
              <Typography variant="body2">{analyticsData.responseTimes.best}h</Typography>
            </div>
            <div>
              <Typography variant="caption">Worst</Typography>
              <Typography variant="body2">{analyticsData.responseTimes.worst}h</Typography>
            </div>
          </Stack>
        </Paper>
      </Grid>

      {/* Main Trend Chart */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 2, height: 400 }}>
          <Typography variant="h6" gutterBottom>
            Damage Frequency Trend
          </Typography>
          <LineChart
            xAxis={[{ 
              data: analyticsData.trends.labels,
              scaleType: 'band'
            }]}
            series={[
              {
                data: analyticsData.trends.highSeverity,
                label: 'High Severity',
                color: theme.palette.error.main
              },
              {
                data: analyticsData.trends.mediumSeverity,
                label: 'Medium Severity',
                color: theme.palette.warning.main
              },
              {
                data: analyticsData.trends.lowSeverity,
                label: 'Low Severity',
                color: theme.palette.success.main
              }
            ]}
          />
        </Paper>
      </Grid>

      {/* Damage Distribution */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, height: 400 }}>
          <Typography variant="h6" gutterBottom>
            Damage Type Distribution
          </Typography>
          <PieChart
            series={[{
              data: analyticsData.distribution,
              innerRadius: 40,
              outerRadius: 80,
              paddingAngle: 5,
              cornerRadius: 5,
            }]}
          />
        </Paper>
      </Grid>

      {/* Geographic Distribution */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 2, height: 400 }}>
          <Typography variant="h6" gutterBottom>
            Geographic Distribution
          </Typography>
          <Box sx={{ height: 300 }}>
            <Suspense fallback={<div>Loading map...</div>}>
              <ReactLeafletMap 
                center={[51.505, -0.09]} 
                zoom={13}
                markers={analyticsData.geoData}
              />
            </Suspense>
          </Box>
        </Paper>
      </Grid>

      {/* Response Time Analysis */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, height: 400 }}>
          <Typography variant="h6" gutterBottom>
            Response Time Analysis
          </Typography>
          <BarChart
            xAxis={[{ 
              scaleType: 'band',
              data: analyticsData.trends.labels 
            }]}
            series={[
              {
                data: [24, 45, 36, 28, 40, 32, 38],
                label: 'Response Time (hours)',
                color: theme.palette.info.main
              }
            ]}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AnalyticsPage;